/**
 * `skaile deploy` — stand a workspace up on a deploy target, then manage its
 * lifecycle through the registry-owned detach handle.
 *
 *   skaile deploy up      [--project-dir <p>] [--target <id>] [--detach]
 *   skaile deploy down    [--project-dir <p>]
 *   skaile deploy status  [--project-dir <p>]
 *   skaile deploy logs    [--project-dir <p>] [--follow]
 *
 * `up` resolves a target via the explicit precedence chain (CLI `--target`
 * override > `skaile.yaml` `deploy:` > built-in `local`), loads any `plugins:`
 * cloud providers, creates the handle, and either holds it (foreground, stops
 * on SIGINT) or persists it (`--detach`). `down` / `status` / `logs` re-attach
 * via `target.restore(payload, ctx)`.
 */

import path from "node:path";
import type { DeployContext, DeployHandle, DeployTarget } from "@skaile/workspaces/plugin-registry";
import { Command } from "commander";
import { logErr, logInfo, logOk, logWarn, S } from "../helpers.ts";

/** Wrap an async action so a thrown error prints via logErr + sets a non-zero exit. */
function action(fn: (...args: never[]) => Promise<void>) {
  return async (...args: unknown[]): Promise<void> => {
    try {
      await (fn as (...a: unknown[]) => Promise<void>)(...args);
    } catch (err) {
      logErr(err instanceof Error ? err.message : String(err));
      process.exitCode = 1;
    }
  };
}

/** Process env narrowed to defined string entries (DeployContext.env is `Record<string,string>`). */
function envRecord(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(process.env)) {
    if (typeof v === "string") out[k] = v;
  }
  return out;
}

interface DeployCtxBundle {
  ctx: DeployContext;
  controller: AbortController;
}

/**
 * Build a `DeployContext` for a project. `workspaceId` is the config `name` or
 * the project dir's basename; `env` is the filtered process env; `log` is a
 * structured logger when available, falling back to a console shim; `signal`
 * comes from a fresh `AbortController`; `wsAuth` is read from
 * `SKAILE_WS_AUTH_TOKEN` when set.
 */
async function buildDeployContext(projectDir: string): Promise<DeployCtxBundle> {
  const { resolveSkWorkspaceConfig } = await import("@skaile/workspaces/core");
  const config = resolveSkWorkspaceConfig(projectDir);
  const workspaceId = config.name ?? path.basename(projectDir);

  let log: DeployContext["log"];
  try {
    const { createLogger } = await import("@skaile/workspaces/core/logging");
    log = createLogger({ kind: "gateway", subkind: "deploy" });
  } catch {
    // No log store registered (standalone CLI) — fall back to a console shim.
    const shim = {
      debug: (m: string) => console.error(S.dim(`  · ${m}`)),
      info: (m: string) => console.error(S.dim(`  · ${m}`)),
      warn: (m: string) => logWarn(m),
      error: (m: string, e?: unknown) =>
        logErr(`${m}${e ? `: ${e instanceof Error ? e.message : String(e)}` : ""}`),
      child: () => shim,
    };
    log = shim as unknown as DeployContext["log"];
  }

  const controller = new AbortController();
  const ctx: DeployContext = {
    workspaceId,
    env: envRecord(),
    log,
    signal: controller.signal,
    ...(process.env.SKAILE_WS_AUTH_TOKEN ? { wsAuth: process.env.SKAILE_WS_AUTH_TOKEN } : {}),
  };
  return { ctx, controller };
}

/** Ensure built-in deploy targets + any `plugins:` cloud providers are registered. */
async function ensureTargetsRegistered(projectDir: string): Promise<void> {
  const { registerBuiltinDeployTargets } = await import("@skaile/workspaces/deploy");
  const { pluginRegistry } = await import("@skaile/workspaces/plugin-registry");
  registerBuiltinDeployTargets(pluginRegistry);

  const { resolveSkWorkspaceConfig } = await import("@skaile/workspaces/core");
  const config = resolveSkWorkspaceConfig(projectDir);
  if (config.plugins && config.plugins.length > 0) {
    const { ensurePluginsLoaded } = await import("../plugin-store/index.ts");
    const result = await ensurePluginsLoaded(projectDir, config.plugins, pluginRegistry);
    for (const f of result.failed) logWarn(f.error);
  }
}

async function runUp(opts: {
  projectDir: string;
  target?: string;
  detach?: boolean;
}): Promise<void> {
  const projectDir = path.resolve(opts.projectDir);
  await ensureTargetsRegistered(projectDir);

  const { resolveSkWorkspaceConfig } = await import("@skaile/workspaces/core");
  const { resolveDeployTarget, pluginRegistry } = await import(
    "@skaile/workspaces/plugin-registry"
  );
  const config = resolveSkWorkspaceConfig(projectDir);

  // Only inherit the yaml `deploy.config` block when the --target override
  // names the same target as the yaml block; a sibling target's config would
  // otherwise be parsed through the wrong schema.
  const overrideConfig = opts.target === config.deploy?.target ? config.deploy?.config : undefined;
  const resolution = await resolveDeployTarget(
    { deploy: config.deploy },
    opts.target
      ? { target: opts.target, config: overrideConfig, reason: "cli --target" }
      : undefined,
    pluginRegistry,
  );

  const { ctx, controller } = await buildDeployContext(projectDir);
  ctx.log.debug(`deploy target resolved`, {
    target: resolution.target.id,
    source: resolution.provenance.source,
    reason: resolution.provenance.reason,
  });

  logInfo(`Deploying to ${S.cmd(resolution.target.id)} (${S.dim(resolution.provenance.source)})…`);
  const handle = await resolution.target.create(resolution.config, ctx);
  await handle.waitReady();

  logOk(`Ready at ${S.cmd(handle.wsUrl)}`);
  console.log(`  ${S.dim("source")}  ${resolution.provenance.source}`);

  if (opts.detach) {
    const { writeHandle } = await import("@skaile/workspaces/deploy");
    await writeHandle(projectDir, resolution.target.id, handle.payload);
    logInfo(`Detached — handle written to ${S.cmd(".skaile/deploy/handle.json")}`);
    return;
  }

  // Foreground: hold the handle and tear down on SIGINT.
  logInfo("Holding deploy in the foreground — press Ctrl-C to stop.");
  await holdUntilSignal(handle, controller);
}

/** Block until SIGINT, then stop the handle and abort the context signal. */
function holdUntilSignal(handle: DeployHandle, controller: AbortController): Promise<void> {
  return new Promise<void>((resolve) => {
    const onSig = () => {
      process.removeListener("SIGINT", onSig);
      controller.abort();
      logInfo("Stopping…");
      handle
        .stop()
        .then(() => logOk("Stopped."))
        .catch((e) => logErr(`stop failed: ${e instanceof Error ? e.message : String(e)}`))
        .finally(() => resolve());
    };
    process.on("SIGINT", onSig);
  });
}

/**
 * Re-attach to a persisted handle: read the envelope, register targets, look up
 * the target by id, and `restore(payload, ctx)`. Returns null (after logging)
 * when no handle exists or the target is unknown.
 */
async function restoreFromHandle(
  projectDir: string,
): Promise<{ target: DeployTarget; handle: DeployHandle; ctx: DeployContext } | null> {
  const { readHandle } = await import("@skaile/workspaces/deploy");
  const stored = await readHandle(projectDir);
  if (!stored) {
    logInfo("No active deploy — nothing to do (no .skaile/deploy/handle.json).");
    return null;
  }

  await ensureTargetsRegistered(projectDir);
  const { pluginRegistry } = await import("@skaile/workspaces/plugin-registry");
  const target = pluginRegistry.get("deployTarget", stored.targetId);
  if (!target) {
    logErr(`unknown deploy target "${stored.targetId}" — is its plugin installed?`);
    return null;
  }

  // Validate the persisted payload against the target's own schema before
  // restore() dereferences it — a corrupted/hand-edited handle.json yields a
  // clean error instead of an unguarded TypeError from inside restore().
  const parsed = target.payloadSchema.safeParse(stored.payload);
  if (!parsed.success) {
    logErr(
      `deploy handle for "${stored.targetId}" is malformed: ${parsed.error.message}. ` +
        `Remove ${S.cmd(".skaile/deploy/handle.json")} to reset.`,
    );
    return null;
  }

  const { ctx } = await buildDeployContext(projectDir);
  const handle = await target.restore(parsed.data, ctx);
  return { target, handle, ctx };
}

async function runDown(opts: { projectDir: string }): Promise<void> {
  const projectDir = path.resolve(opts.projectDir);
  const restored = await restoreFromHandle(projectDir);
  if (!restored) return;

  await restored.handle.stop();
  const { clearHandle } = await import("@skaile/workspaces/deploy");
  await clearHandle(projectDir);
  logOk(`Stopped ${S.cmd(restored.target.id)} and cleared the handle.`);
}

async function runStatus(opts: { projectDir: string }): Promise<void> {
  const projectDir = path.resolve(opts.projectDir);
  const restored = await restoreFromHandle(projectDir);
  if (!restored) return;

  const health = await restored.handle.health();
  console.log();
  console.log(`  ${S.label("target")}   ${S.cmd(restored.target.id)}`);
  console.log(`  ${S.label("state")}    ${restored.handle.state}`);
  console.log(`  ${S.label("wsUrl")}    ${S.cmd(restored.handle.wsUrl)}`);
  console.log(
    `  ${S.label("health")}   ${health.healthy ? S.ok("healthy") : S.err("unhealthy")}${health.detail ? ` ${S.dim(`(${health.detail})`)}` : ""}`,
  );
  console.log();
}

async function runLogs(opts: { projectDir: string; follow?: boolean }): Promise<void> {
  const projectDir = path.resolve(opts.projectDir);
  const restored = await restoreFromHandle(projectDir);
  if (!restored) return;

  if (!restored.handle.logs) {
    logInfo(`logs not supported by target ${S.cmd(restored.target.id)}`);
    return;
  }

  for await (const entry of restored.handle.logs({ follow: opts.follow })) {
    console.log(entry.line);
  }
}

/**
 * Creates the `skaile deploy` command group.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/deploy#make-deploy-command
 */
export function makeDeployCommand(): Command {
  const cmd = new Command("deploy").description(
    "Deploy a workspace to a target (up / down / status / logs)",
  );

  cmd
    .command("up")
    .description("Resolve a deploy target, stand the workspace up, and wait until ready")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .option("--target <id>", "Override the deploy target (precedence: override > yaml > default)")
    .option("--detach", "Persist the handle and return instead of holding in the foreground")
    .action(action(runUp as never));

  cmd
    .command("down")
    .description("Tear down the active deploy and clear its handle")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(action(runDown as never));

  cmd
    .command("status")
    .description("Show the active deploy's state, ws URL, and health")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(action(runStatus as never));

  cmd
    .command("logs")
    .description("Stream logs from the active deploy (if the target supports it)")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .option("--follow", "Keep streaming new log lines")
    .action(action(runLogs as never));

  cmd.action(() => cmd.help());
  return cmd;
}
